import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  spreedlyCvvName,
  spreedlyIframeFields,
  spreedlyNumberName,
} from './spreedly-form.component';
import SpreedlyFormContext from './spreedly-form.context';
import { useTheme } from '@mui/material';
import i18n from 'i18n';
import { useHandleOrderStatus } from 'pages/checkout/checkout-buttons-new/checkout-buttons-new.hooks';

const inputStyle = `width: 100%; height: 100%; font-size: 16px; padding: 3px 0px;`;

export const useSpreedlyParameters = (initOrderAttributes = {}) => {
  const { nonce, timestamp, certificate_token, signature } =
    initOrderAttributes;

  const parameters = {
    numberEl: spreedlyNumberName,
    cvvEl: spreedlyCvvName,
  };

  parameters.nonce = nonce;
  parameters.timestamp = timestamp;
  parameters.certificateToken = certificate_token;
  parameters.signature = signature;

  return parameters;
};

export const useSpreedlyForm = (
  initOrderAttributes,
  onSuccess,
  onError,
  paymentInstrumentToRecache,
) => {
  const envKey = initOrderAttributes?.SPREEDLY_ENV_KEY;
  const parameters = useSpreedlyParameters(initOrderAttributes);
  const handleRecacheSuccess = useHandleRecacheSuccess();

  const [errors, setErrors] = useState({});
  const form = useFormContext();

  useEffect(() => {
    window.Spreedly.init(envKey, parameters);

    window.Spreedly.on('ready', () => {
      setSpreedlyStylesOnReady();
      if (paymentInstrumentToRecache)
        recachePaymentInstrument(paymentInstrumentToRecache);
    });

    if (paymentInstrumentToRecache) handleRecacheSuccess(onSuccess);
    else window.Spreedly.on('paymentMethod', onSuccess);

    handleSpreedlyValidation(form, setErrors, !!paymentInstrumentToRecache);

    window.Spreedly.on('errors', handleErrors(onError, setErrors, form));

    return () => {
      window.Spreedly.removeHandlers();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters?.timestamp]);

  useSpreedlyStyles();

  return { errors };
};

const setFieldValidation = (name, validation) => {
  if (name === 'number') {
    return validation.validNumber
      ? null
      : i18n.t('checkout.payment.spreedly.errors.number');
  }
  if (name === 'cvv') {
    return validation.validCvv
      ? null
      : i18n.t('checkout.payment.spreedly.errors.cvv');
  }
};

export const useSpreedlyIframeField = name => {
  const { errors } = useContext(SpreedlyFormContext);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(errors[spreedlyIframeFields[name]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return { error };
};

export const handleErrors = (callback, setErrors, form) => errors => {
  callback(errors);
  errors.forEach(error => {
    if (error.attribute === 'number' || error.attribute === 'cvv') {
      setErrors(prev => ({
        ...prev,
        [error.attribute]: error.message,
      }));
      window.Spreedly.transferFocus(error.attribute);
    } else if (!!error.attribute) {
      form.setError(error.attribute, {
        type: 'manual',
        message: error.message,
      });
    } else {
      form.setError(`global.${error.key}`, {
        type: 'global',
        key: error.key,
        message: error.message,
      });
    }
  });
};

const setSpreedlyStylesOnReady = () => {
  window.Spreedly.setStyle('number', inputStyle);
  window.Spreedly.setStyle('cvv', inputStyle);
  window.Spreedly.setRequiredAttribute('number');
  window.Spreedly.setRequiredAttribute('cvv');
};

const handleSpreedlyValidation = (
  form,
  setErrors,
  paymentInstrumentToRecache,
) => {
  let previousFieldName;
  window.Spreedly.on('fieldEvent', (name, event, _activeElement, inputData) => {
    if (typeof inputData.validCvv !== 'undefined') {
      form.setValue('isValidCVV', inputData.validCvv);
    }
    if (event === 'blur') {
      previousFieldName = name;
      window.Spreedly.validate();
    }
  });

  window.Spreedly.on('validation', validation => {
    setErrors(prev => {
      if (previousFieldName) {
        return {
          ...prev,
          [previousFieldName]: setFieldValidation(
            previousFieldName,
            validation,
          ),
        };
      }
      return {
        ...prev,
        number: paymentInstrumentToRecache
          ? null
          : setFieldValidation('number', validation),
        cvv: setFieldValidation('cvv', validation),
      };
    });
  });
};

export const useSpreedlyStyles = () => {
  const theme = useTheme();

  useEffect(() => {
    if (theme.palette.type === 'dark') {
      window.Spreedly.setStyle(
        'number',
        `${inputStyle};color: ${theme.palette.text.primary};`,
      );
      window.Spreedly.setStyle(
        'cvv',
        `${inputStyle};color: ${theme.palette.text.primary};`,
      );
    } else {
      window.Spreedly.setStyle('number', inputStyle);
      window.Spreedly.setStyle('cvv', inputStyle);
    }
  }, [theme.palette.text.primary, theme.palette.type]);
};

export const recachePaymentInstrument = paymentInstrument => {
  window.Spreedly.setRecache(paymentInstrument.paymentToken, {
    card_type: paymentInstrument.cardType,
    last_four_digits: paymentInstrument.lastFour,
  });
};

const useHandleRecacheSuccess = () => {
  const handleOrderStatus = useHandleOrderStatus();
  const handleRecacheSuccess = callback => {
    window.Spreedly.on('recache', async (_token, _paymentMethod) => {
      try {
        await callback?.();
      } catch (error) {
        handleOrderStatus(error);
      }
    });
  };

  return handleRecacheSuccess;
};

export const spreedlyFormSubmit = (rhfHandleSubmit, onSuccess) => {
  return rhfHandleSubmit(
    data => {
      if (!data.isValidCVV) {
        window.Spreedly.transferFocus('cvv');
        window.Spreedly.validate();
      } else {
        onSuccess(data);
      }
    },
    () => {
      window.Spreedly.validate();
    },
  );
};
